<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Sub-departments"
      subtitle="Organizational units within departments"
      icon="pi-sitemap"
    >
      <template #actions>
        <Button
          label="Add"
          icon="pi pi-plus"
          size="small"
          :disabled="mutating"
          @click="openCreate"
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadItems"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body">
      <div v-if="loading && !items.length" class="settings-org-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-org-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadItems" />
        </div>
      </Message>

      <template v-else>
        <SettingsOrgReferenceToolbar
          v-model:show-inactive="showInactive"
          v-model:search-query="searchQuery"
          :count-label="countLabel"
          inactive-input-id="sub-departments-show-inactive"
          search-placeholder="Search sub-departments..."
          :loading="loading"
        />

        <div v-if="!filteredItems.length" class="settings-org-section__empty">
          <i class="pi pi-sitemap settings-org-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="searchQuery"
            label="Clear search"
            size="small"
            severity="secondary"
            text
            @click="searchQuery = ''"
          />
        </div>

        <DataTable
          v-else
          :value="filteredItems"
          size="small"
          dataKey="id"
          sort-mode="single"
          removable-sort
          :sort-field="sortField"
          :sort-order="sortOrder"
          @sort="onSort"
          :rowClass="rowClass"
        >
          <Column field="name" header="Name" sortable>
            <template #body="{ data }">
              <span class="font-medium">{{ data.name }}</span>
            </template>
          </Column>
          <Column v-if="showInactive" header="Status" style="width: 6.5rem">
            <template #body="{ data }">
              <SettingsOrgReferenceStatusTag :item="data" />
            </template>
          </Column>
          <Column field="department_label" header="Department" sortable style="min-width: 10rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">
                {{ data.department_label }}
              </span>
            </template>
          </Column>
          <Column field="parent_label" header="Parent" sortable style="min-width: 9rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">
                {{ data.parent_label }}
              </span>
            </template>
          </Column>
          <Column header="Actions" style="width: 11rem">
            <template #body="{ data }">
              <div class="crc-table-row-actions">
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Edit'"
                  :disabled="mutating"
                  @click="openEdit(data)"
                />
                <Button
                  v-if="isOrgReferenceActive(data)"
                  icon="pi pi-ban"
                  size="small"
                  severity="warn"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Deactivate'"
                  :disabled="mutating"
                  @click="confirmDeactivate(data)"
                />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Delete'"
                  :disabled="mutating"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </div>

    <SubDepartmentEditModal
      ref="editModalRef"
      v-model:visible="editModalVisible"
      :saving="mutating"
      :departments="departments"
      :sub-departments="items"
      @save="handleSave"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';
import SettingsOrgReferenceStatusTag from '../SettingsOrgReferenceStatusTag.vue';
import SubDepartmentEditModal from '../modals/SubDepartmentEditModal.vue';
import { useSettingsOrgReferenceList } from '../../composables/useSettingsOrgReferenceList';
import {
  buildOrgReferenceCountLabel,
  isOrgReferenceActive,
  orgReferenceRowClass,
  sortOrgReferenceRows,
} from '../../utils/settingsOrgReferenceUtils';
import { orgReferenceApi } from '@/api/orgReferenceApi';
import { useClientTableSort } from '@/utils/tableSort';

defineOptions({ name: 'SettingsSubDepartmentsSection' });

const { sortField, sortOrder, onSort } = useClientTableSort('name');

defineProps({
  section: { type: Object, default: null },
});

defineEmits(['close-settings']);

const toast = useToast();
const confirm = useConfirm();
const { showInactive, fetchOptions, afterMutation } = useSettingsOrgReferenceList();

const items = ref([]);
const departments = ref([]);
const loading = ref(false);
const error = ref('');
const mutating = ref(false);
const searchQuery = ref('');
const editModalVisible = ref(false);
const editModalRef = ref(null);

const departmentMap = computed(() => {
  const map = new Map();
  departments.value.forEach((dept) => map.set(dept.id, dept.name));
  return map;
});

const itemMap = computed(() => {
  const map = new Map();
  items.value.forEach((item) => map.set(item.id, item.name));
  return map;
});

const filteredItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const base = query
    ? items.value.filter((item) => {
        const haystack = [
          item.name,
          departmentName(item.department_id),
          parentName(item.parent_id),
          isOrgReferenceActive(item) ? 'active' : 'inactive',
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
    : items.value;
  return sortOrgReferenceRows(base).map((item) => ({
    ...item,
    department_label: departmentName(item.department_id),
    parent_label: parentName(item.parent_id),
  }));
});

const countLabel = computed(() =>
  buildOrgReferenceCountLabel(items.value, 'sub-departments', showInactive.value)
);

const emptyMessage = computed(() => {
  if (searchQuery.value) return 'No sub-departments match your search.';
  if (showInactive.value) return 'No sub-departments found. Click Add to create one.';
  return 'No active sub-departments yet. Click Add to create one.';
});

const rowClass = (data) => orgReferenceRowClass(data);

const departmentName = (id) => departmentMap.value.get(id) || '—';
const parentName = (id) => (id ? itemMap.value.get(id) || '—' : '—');

const notifySuccess = (detail) => {
  toast.add({ severity: 'success', summary: 'Success', detail, life: 2500 });
};

const notifyError = (err, fallback) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: err?.message || fallback,
    life: 3500,
  });
};

const afterMutationReload = () => afterMutation(loadItems);

const loadItems = async () => {
  loading.value = true;
  error.value = '';
  try {
    const options = await fetchOptions();
    items.value = Array.isArray(options?.sub_departments)
      ? sortOrgReferenceRows(options.sub_departments)
      : [];
    departments.value = Array.isArray(options?.departments) ? options.departments : [];
  } catch (err) {
    error.value = err?.message || 'Failed to load sub-departments.';
    items.value = [];
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  editModalRef.value?.openNew?.();
  editModalVisible.value = true;
};

const openEdit = (item) => {
  editModalRef.value?.openEdit?.(item);
  editModalVisible.value = true;
};

const handleSave = async ({ mode, payload }) => {
  mutating.value = true;
  try {
    if (mode === 'new') {
      await orgReferenceApi.subDepartments.create(payload);
      notifySuccess('Sub-department created');
    } else {
      await orgReferenceApi.subDepartments.update(payload);
      notifySuccess('Sub-department updated');
    }
    editModalVisible.value = false;
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to save sub-department.');
  } finally {
    mutating.value = false;
  }
};

const confirmDeactivate = (item) => {
  confirm.require({
    header: 'Deactivate sub-department',
    message: `"${item.name}" will be hidden from profile options. Linked employees and teams keep their records.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-warning',
    accept: () => deactivateItem(item),
  });
};

const deactivateItem = async (item) => {
  mutating.value = true;
  try {
    await orgReferenceApi.subDepartments.update({
      sub_department_id: item.id,
      is_active: 0,
    });
    notifySuccess('Sub-department deactivated');
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to deactivate sub-department.');
  } finally {
    mutating.value = false;
  }
};

const confirmDelete = (item) => {
  confirm.require({
    header: 'Delete sub-department',
    message: `Delete "${item.name}"? Employee and team links will be cleared. Child sub-departments lose their parent link. This cannot be undone.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => deleteItem(item),
  });
};

const deleteItem = async (item) => {
  mutating.value = true;
  try {
    await orgReferenceApi.subDepartments.delete({ sub_department_id: item.id });
    notifySuccess('Sub-department deleted');
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to delete sub-department.');
  } finally {
    mutating.value = false;
  }
};

onMounted(() => {
  loadItems();
});

watch(showInactive, () => {
  loadItems();
});
</script>

<style scoped>
.settings-org-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-org-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-org-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-org-section__message {
  margin: 0;
}

.settings-org-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  text-align: center;
}

.settings-org-section__empty-icon {
  font-size: 1.5rem;
}

.settings-org-section__empty p {
  margin: 0;
  font-size: 0.875rem;
}

:deep(.settings-org-section__row--inactive) {
  opacity: 0.65;
}
</style>
